import type { ReactElement } from 'react';

/** Bộ icon stroke tối giản của Torii (xem readme design system §ICONOGRAPHY). */
export type IconKey =
  | 'learn' | 'review' | 'community' | 'chat' | 'ai' | 'profile' | 'kana' | 'deck' | 'book' | 'chart' | 'gear' | 'heart'
  | 'search' | 'chev' | 'back' | 'close' | 'check' | 'lock' | 'warn' | 'flag' | 'play' | 'mic' | 'edit';

type IconFn = (c?: string, s?: number) => ReactElement;

const stroke = (d: ReactElement, c: string, s: number, w = 1.9) => (
  <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth={w} strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);

export const WEB_ICO: Record<IconKey, IconFn> = {
  learn: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v15H6.5A2.5 2.5 0 0 0 4 20.5Z" />
        <path d="M4 20.5A2.5 2.5 0 0 1 6.5 18H20v3H6.5" />
      </>,
      c,
      s
    ),
  review: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <rect x="3" y="7" width="13" height="13" rx="2" />
        <path d="M8 4h11a2 2 0 0 1 2 2v11" />
      </>,
      c,
      s
    ),
  community: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <circle cx="9" cy="8" r="3.5" />
        <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
        <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8M18 14a6.5 6.5 0 0 1 3.5 6" />
      </>,
      c,
      s
    ),
  chat: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        <path d="M8 9h8M8 13h5" />
      </>,
      c,
      s
    ),
  ai: (c = 'currentColor', s = 20) =>
    stroke(<path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.4 8.5 8.5 0 0 1-4-1L3 20l1.1-5.5A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z" />, c, s),
  profile: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
      </>,
      c,
      s
    ),
  kana: (c = 'currentColor', s = 20) => (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: s,
        height: s,
        fontSize: s * 0.85,
        fontWeight: 700,
        color: c,
        fontFamily: 'var(--font-jp, sans-serif)',
        lineHeight: 1,
      }}
    >
      あ
    </span>
  ),
  deck: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M7 20h10" />
      </>,
      c,
      s
    ),
  book: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <path d="M12 6.5A4.5 4.5 0 0 0 7.5 4H4v14h3.5A4.5 4.5 0 0 1 12 20.5" />
        <path d="M12 6.5A4.5 4.5 0 0 1 16.5 4H20v14h-3.5A4.5 4.5 0 0 0 12 20.5" />
      </>,
      c,
      s
    ),
  heart: (c = 'currentColor', s = 14) =>
    stroke(<path d="M19 5.7a4.6 4.6 0 0 0-6.5 0l-.5.5-.5-.5A4.6 4.6 0 0 0 5 12.2l6.5 6.6a.7.7 0 0 0 1 0l6.5-6.6a4.6 4.6 0 0 0 0-6.5Z" />, c, s, 2),
  chart: (c = 'currentColor', s = 20) => stroke(<path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />, c, s),
  gear: (c = 'currentColor', s = 20) =>
    stroke(
      <>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6h.09A1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
      </>,
      c,
      s,
      1.8
    ),
  search: (c = 'currentColor', s = 18) =>
    stroke(
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
      </>,
      c,
      s,
      2
    ),
  chev: (c = 'var(--navy-300)', s = 18) => stroke(<path d="M9 6l6 6-6 6" />, c, s, 2),
  back: (c = 'var(--ink)', s = 20) => stroke(<path d="M15 18l-6-6 6-6" />, c, s, 2),
  close: (c = 'var(--ink)', s = 20) => stroke(<path d="M18 6 6 18M6 6l12 12" />, c, s, 2),
  check: (c = '#fff', s = 18) => stroke(<path d="M20 6 9 17l-5-5" />, c, s, 3),
  lock: (c = 'var(--navy-700)', s = 18) =>
    stroke(
      <>
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </>,
      c,
      s,
      2
    ),
  warn: (c = 'var(--warning-text)', s = 16) =>
    stroke(
      <>
        <path d="M12 9v4M12 17h.01" />
        <path d="M10.3 3.9 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      </>,
      c,
      s,
      2
    ),
  flag: (c = 'var(--cta-default)', s = 17) =>
    stroke(
      <>
        <path d="M4 21V4" />
        <path d="M4 4h11l-1.5 4L15 12H4" />
      </>,
      c,
      s,
      2
    ),
  play: (c = '#fff', s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill={c}>
      <path d="M7 4l13 8-13 8z" />
    </svg>
  ),
  mic: (c = 'currentColor', s = 22) =>
    stroke(
      <>
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10v2a7 7 0 0 0 14 0v-2M12 19v3" />
      </>,
      c,
      s,
      2
    ),
  edit: (c = 'currentColor', s = 13) =>
    stroke(
      <>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </>,
      c,
      s,
      2
    ),
};
